import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import { PriceTagIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTravelCompanies } from '../../../api/travel-companies/get-travel-companies'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { getTripBooking } from '../../../api/trip-bookings/get-trip-booking'
import { getTripBookings } from '../../../api/trip-bookings/get-trip-bookings'
import { getTripProviders } from '../../../api/trip-providers/get-trip-providers'
import { getTrips } from '../../../api/trips/get-trips'
import { getUsers } from '../../../api/user/get-users'
import {
  TransportTypeEnum,
  transportTypeMapping
} from '../../../lib/types/enums/trip/trip-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import { ItemNotFound } from '../../elements/item-not-found'
import ValidationButtons from '../../elements/validation-buttons'
import { TripBookingItem } from './trip-booking.ui'
import { TRIP_BOOKING_FORM_SCHEMA_VALIDATION } from './utils'

const TripBookingForm = ({
  tripBookingId,
  defaultValues,
  onDirtyChange,
  ...validationButtonProps
}: BaseFormProps<TripBookingTypes.Input> & {
  tripBookingId?: number
  onDirtyChange?: (isDirty: boolean) => void
}) => {
  const form = useForm<TripBookingTypes.Input>({
    resolver: zodResolver(TRIP_BOOKING_FORM_SCHEMA_VALIDATION),
    defaultValues: { isTakeInCharge: false, ...defaultValues },
    mode: FORM_MODE
  })

  const { isTakeInCharge, totalPrice, numberOfStudentsPlacesReserved } = form.watch()
  const { isDirty, isSubmitSuccessful } = form.formState
  // The "Prise en charge" toggle resets the form, so it is tracked apart from isDirty
  const [isTakeInChargeToggled, setIsTakeInChargeToggled] = useState(false)

  // Once saved, the submitted values become the new reference so only later edits count as changes
  useEffect(() => {
    if (!isSubmitSuccessful) return
    form.reset(form.getValues())
    setIsTakeInChargeToggled(false)
  }, [isSubmitSuccessful])

  useEffect(() => {
    onDirtyChange?.(isDirty || isTakeInChargeToggled)
  }, [isDirty, isTakeInChargeToggled])

  const { data, isPending } = useQuery({
    queryKey: ['trip-booking', tripBookingId],
    queryFn: () => {
      if (tripBookingId) return getTripBooking(tripBookingId, 'PARTIAL')
    },
    enabled: !!tripBookingId
  })

  const { data: travelCompanies } = useQuery({
    queryKey: ['travel-companies'],
    queryFn: () => getTravelCompanies(),
    enabled: !isTakeInCharge
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } }),
    enabled: !isTakeInCharge
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans(),
    enabled: !isTakeInCharge
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips({ filter: { linkedToMainTrip: false, hasTransport: true } })
  })

  const { data: tripProviders } = useQuery({
    queryKey: ['trip-providers'],
    queryFn: () => getTripProviders(),
    enabled: !isTakeInCharge
  })

  const { data: tripBookings } = useQuery({
    queryKey: ['trip-bookings', 'isTakeInCharge-false'],
    queryFn: () => getTripBookings({ filter: { isTakeInCharge: false } }),
    enabled: !isTakeInCharge
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses(),
    enabled: isTakeInCharge
  })

  function handleFormChange() {
    setIsTakeInChargeToggled(true)
    form.reset({
      isTakeInCharge: !isTakeInCharge,
      lecResponsableId: undefined,
      tripProviderId: undefined,
      reservationNumber: undefined,
      totalPrice: undefined,
      label: undefined,
      date: undefined,
      meetingPlaceId: undefined,
      meetingTimeForParents: undefined,
      meetingTimeForStaff: undefined,
      mainReservationId: undefined,
      transportMeansId: undefined,
      travelCompany: undefined
    })
  }

  if (tripBookingId && !data && !isPending)
    return <ItemNotFound title={"La réservation que vous cherchez n'existe pas"} />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Réservation
          </Typography>

          <FormField
            control={form.control}
            name="isTakeInCharge"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    label={'Prise en charge'}
                    onChange={handleFormChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {isTakeInCharge ? (
            <FormField
              control={form.control}
              name={'label'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} label={'Libellé'} type="text" required />
                  </FormControl>
                </FormItem>
              )}
            />
          ) : (
            <>
              <FormField
                control={form.control}
                name={'travelCompany'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        optional
                        searchable
                        allowNewOption
                        label={'Compagnie'}
                        options={travelCompanies?.data.map(({ name }) => ({
                          label: name,
                          value: name
                        }))}
                        selectedOption={getSelectedOption(field.value, field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'lecResponsableId'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        label={'Responsable LEC'}
                        options={users?.data.map((user) => ({
                          label: toFullName(user.firstName, user.lastName),
                          value: user.id
                        }))}
                        selectedOption={getSelectedOption(
                          field.value,
                          toFullName(
                            users?.data.find((user) => user.id === field.value)?.firstName,
                            users?.data.find((user) => user.id === field.value)?.lastName
                          )
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'transportMeansId'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        label={'Moyen de transport'}
                        options={transportMeans?.data.map((transportMean) => ({
                          label: getTranslatedName(transportMean),
                          value: transportMean.id
                        }))}
                        selectedOption={conditionalRender(!!field?.value, {
                          label: getTranslatedName(
                            transportMeans?.data.find(
                              (transportMean) => transportMean.id === field.value
                            )
                          ),
                          value: field.value
                        })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}

          <div className="flex gap-4">
            {Object.values(TransportTypeEnum)
              .filter((value) => (!isTakeInCharge ? true : value !== TransportTypeEnum.ROUND_TRIP))
              .map((transportType) => (
                <FormField
                  key={transportType}
                  control={form.control}
                  name={'directionality'}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={transportTypeMapping[transportType].label}
                            checked={field.value === transportType}
                            onCheck={() => field.onChange(transportType)}
                          />
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />
              ))}
          </div>

          <div className="flex gap-4 justify-between">
            <FormField
              control={form.control}
              name={'relatedTripId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={'Voyage'}
                      options={trips?.data.map((trip) => ({
                        label: TripProperties.getTripLabel(trip),
                        value: trip.id
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        label: TripProperties.getTripLabel(
                          trips?.data.find((trip) => trip.id === field.value)
                        ),
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!isTakeInCharge && (
              <FormField
                control={form.control}
                name={'tripProviderId'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        searchable
                        label={'Prestataire voyage'}
                        options={tripProviders?.data.map((tripProvider) => ({
                          label: tripProvider.companyName,
                          value: tripProvider.id
                        }))}
                        selectedOption={conditionalRender(!!field?.value, {
                          label:
                            tripProviders?.data.find(
                              (tripProvider) => tripProvider.id === field.value
                            )?.companyName ?? '',
                          value: field.value
                        })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>

          {isTakeInCharge && (
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name={'date'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledDateStringInput {...field} label={'Date'} required />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'meetingPlaceId'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        searchable
                        label={'Lieu de rendez-vous'}
                        options={tripAddresses?.data.map((tripAddress) => ({
                          label: tripAddress.label,
                          value: tripAddress.id
                        }))}
                        selectedOption={conditionalRender(!!field?.value, {
                          label:
                            tripAddresses?.data.find(
                              (tripAddress) => tripAddress.id === field.value
                            )?.label ?? '',
                          value: field.value
                        })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'meetingTimeForParents'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput {...field} required label={'Heure de RDV parents'} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'meetingTimeForStaff'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput
                        {...field}
                        required={!isTakeInCharge}
                        label={'Heure de RDV encadrants'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}

          <div className="flex gap-4 justify-between">
            {!isTakeInCharge && (
              <FormField
                control={form.control}
                name={'reservationNumber'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        label={'Numéro de réservation'}
                        type="text"
                        required
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name={'numberOfStudentsPlacesReserved'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      label={'Nombre de jeunes'}
                      type="number"
                      isControllable
                      required
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'numberOfStaffPlacesReserved'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      label={`Nombre d'encadrants`}
                      type="number"
                      isControllable
                      required={!isTakeInCharge}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {!isTakeInCharge && (
            <>
              <div className="flex gap-4 items-center">
                <FormField
                  control={form.control}
                  name={'totalPrice'}
                  render={({ field }) => (
                    <FormItem className={'w-1/2'}>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          label={`Prix (€)`}
                          type={'number'}
                          icon={PriceTagIcon}
                          required
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex gap-4 items-center">
                  <Typography variant={'secondary-body'}>Prix par participant</Typography>

                  {totalPrice && numberOfStudentsPlacesReserved && (
                    <Badge
                      variant={'primary'}
                      text={`${(totalPrice / numberOfStudentsPlacesReserved).toFixed(2)} €`}
                    />
                  )}
                </div>
              </div>

              {data &&
                (data?.secondaryReservation ? (
                  <div className={'flex gap-3 items-center flex-row'}>
                    <Typography variant={'secondary-body'} color={tailwindColors.grey.DEFAULT}>
                      Réservation fille
                    </Typography>

                    <div className="flex gap-2 items-center flex-row bg-white-hover border rounded border-grey-light px-3 py-2">
                      <TripBookingItem
                        type={'secondary'}
                        displayViewButton
                        tripBooking={data}
                        options={{ count: ['reserved-allocated'], displayLabel: false }}
                      />
                    </div>
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name={'mainReservationId'}
                    render={({ field }) => (
                      <FormItem className={'flex-1'}>
                        <FormControl>
                          <ControlledSelectInput
                            {...field}
                            searchable
                            optional
                            label={'Réservation mère'}
                            // remove current reservation from the list
                            options={tripBookings?.data
                              .filter((tripBooking) => tripBooking.id !== data.id)
                              .map((tripBooking) => ({
                                label: TripBookingProperties.getLabel(tripBooking, {
                                  displayDate: true
                                }),
                                value: tripBooking.id
                              }))}
                            selectedOption={conditionalRender(!!field?.value, {
                              label: TripBookingProperties.getLabel(
                                tripBookings?.data.find(
                                  (tripBooking) => tripBooking.id === field.value
                                ) ?? ({} as TripBookingTypes.Light),
                                { displayDate: true }
                              ),
                              value: field.value
                            })}
                          />
                        </FormControl>
                        <Typography
                          variant={'small-medium'}
                          color={tailwindColors.grey.light.active}
                        >
                          Lier cette réservation à une réservation précédente
                        </Typography>
                      </FormItem>
                    )}
                  />
                ))}
            </>
          )}
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default TripBookingForm
